import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Radio, Space } from 'antd';

import { type OnboardingStep } from '@/entities/onboarding';

import { logError } from '@/shared/lib/logging';
import { useAppDispatch } from '@/shared/lib/store';

import { useTradingLevel } from '../../../model/selectors/onboarding.selectors';
import { setTradingLevel } from '../../../model/slices/onboarding.slice';
import { OnboardingStepWrapper } from '../../onboarding-step-wrapper/onboarding-step-wrapper';

interface TradingLevelOnboardingStepProps {
    step: OnboardingStep;
    onNext?: (() => void) | undefined;
    isLastStep?: boolean | undefined;
    isProcessing?: boolean | undefined;
}

export const TradingLevelOnboardingStep = memo(
    (props: TradingLevelOnboardingStepProps) => {
        const { step, onNext, isLastStep, isProcessing } = props;
        const { t } = useTranslation('onboarding');
        const dispatch = useAppDispatch();
        const [form] = Form.useForm();

        // Get existing data from store
        const existingTradingLevel = useTradingLevel();

        // Define trading levels with translations
        const tradingLevels = [
            {
                value: 'beginner',
                label: t('tradingLevel.levels.beginner'),
            },
            {
                value: 'intermediate',
                label: t('tradingLevel.levels.intermediate'),
            },
            {
                value: 'advanced',
                label: t('tradingLevel.levels.advanced'),
            },
            {
                value: 'expert',
                label: t('tradingLevel.levels.expert'),
            },
        ];

        // Initialize form with existing data
        useEffect(() => {
            form.setFieldsValue({
                trading_level: existingTradingLevel,
            });
        }, [form, existingTradingLevel]);

        const handleSubmit = async () => {
            try {
                const values = await form.validateFields();

                dispatch(
                    setTradingLevel({
                        trading_level: values.trading_level,
                    }),
                );

                onNext?.();
            } catch (error) {
                logError('Form validation failed:', error);
            }
        };

        return (
            <OnboardingStepWrapper
                isLastStep={isLastStep}
                isProcessing={isProcessing}
                onNext={handleSubmit}
                step={step}
            >
                <Form
                    form={form}
                    layout="vertical"
                    style={{ width: '100%' }}
                >
                    <Form.Item
                        label={t('tradingLevel.question')}
                        name="trading_level"
                        rules={[
                            {
                                required: true,
                                message: t('tradingLevel.required'),
                            },
                        ]}
                    >
                        <Radio.Group style={{ width: '100%' }}>
                            <Space
                                direction="vertical"
                                style={{ width: '100%' }}
                            >
                                {tradingLevels.map((level) => (
                                    <Radio
                                        key={level.value}
                                        value={level.value}
                                    >
                                        {level.label}
                                    </Radio>
                                ))}
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                </Form>
            </OnboardingStepWrapper>
        );
    },
);

TradingLevelOnboardingStep.displayName = 'TradingLevelOnboardingStep';
